package nl.volleyplanner.web.component.backend_jobs

import kotlinx.coroutines.*
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import nl.volleyplanner.api.backend_jobs.JobExecution
import nl.volleyplanner.api.backend_jobs.JobStatus
import nl.volleyplanner.api.backend_jobs.JobType
import nl.volleyplanner.util.CoroutineSafeMap
import nl.volleyplanner.web.CoroutineManager
import nl.volleyplanner.web.IdGenerator
import nl.volleyplanner.web.Logger
import nl.volleyplanner.web.VolleyPlannerApplication


data class JobFlowState(
    val jobType: JobType,
    val executionId: String? = null,
    val jobExecution: JobExecution? = null,
    val pollIndex: Int = 0
) {
    val jobResult = jobExecution?.result

    val jobStatus = when {
        jobExecution?.cloudJobExecution != null -> jobExecution.cloudJobExecution!!.jobStatus
        jobExecution?.backendJobExecution != null -> jobExecution.backendJobExecution!!.jobStatus
        else -> JobStatus.Pending
    }
    fun isScheduled() = executionId != null || jobExecution?.backendJobExecution?.identifier != null
    fun isCompleted() = jobExecution?.status == JobStatus.Finished
    fun isRunning() = isScheduled() && !isCompleted()
}

open class BackendJobTrackerListener() {
    fun onComplete(jobExecution: JobExecution) {}
    fun onStatusUpdate(jobExecution: JobExecution) {}
}

class BackendJobTracker(val jobType: JobType, val executionId: String, initialState: JobExecution?) {
    private val logger = Logger.getLogger("nl.volleyplanner.web.component.backend_jobs.BackendJobTracker")

    val trackerId = IdGenerator.nextId()

    private val _stateFlow = MutableStateFlow<JobFlowState>(JobFlowState(jobType, executionId, initialState))
    val stateFlow: StateFlow<JobFlowState> = _stateFlow.asStateFlow()

    private val statusListeners = mutableListOf<BackendJobTrackerListener>()

    private var trackerJob: Job? = null

    init {
        if (initialState == null || (initialState.cloudJobExecution?.isCompleted() == false || initialState.backendJobExecution?.jobStatus != JobStatus.Finished)) {
            trackerJob = CoroutineManager.DISPATCHER_SCOPE.launch {
                if (initialState == null) updateStatus()
                while (stateFlow.value.jobStatus != JobStatus.Finished) {
                    delay(10000)
                    updateStatus()
                }
                logger.debug("[$jobType:$trackerId] Job for $executionId is finished, stop tracking")
            }
        }
    }

    fun addStatusListener(listener: BackendJobTrackerListener) {
        statusListeners.add(listener)
    }

    private suspend fun updateStatus() {
        logger.info("[$jobType:$trackerId] Retrieving job status for execution $executionId")
        val status = VolleyPlannerApplication.volleyplannerApiClient.getJobStatus(jobType, executionId)
        val hasCompleted = status.status == JobStatus.Finished && _stateFlow.value.jobStatus != JobStatus.Finished
        _stateFlow.value = _stateFlow.value.copy(jobExecution = status)
        statusListeners.forEach {
            if (!hasCompleted) {
                it.onStatusUpdate(status)
            } else {
                it.onComplete(status)
            }
        }
    }

    fun cancelTracking() {
        trackerJob?.cancel()
        trackerJob = null
    }
}

object BackendJobManager {
    private val logger = Logger.getLogger("nl.volleyplanner.web.component.backend_jobs.BackendJobManager")
    private val activeTrackers = CoroutineSafeMap<JobType, BackendJobTracker>()

    val mutex = Mutex()

    suspend fun getAndMonitorLastJob(jobType: JobType) : BackendJobTracker? {
        mutex.withLock {
            logger.debug("getAndMonitorLastJob: $jobType. Activetrackers: ${activeTrackers.getEntries().map { "${it.key.name}:${it.value.trackerId}" }}")
            val currentTracker = activeTrackers.get(jobType)
            if (currentTracker != null && !currentTracker.stateFlow.value.isCompleted()) {
                logger.debug("Returning existing tracker for $jobType: ${currentTracker.trackerId}")
                return currentTracker
            }
            val lastJob = VolleyPlannerApplication.volleyplannerApiClient.getLastJob(jobType)
            if (lastJob != null) {
                if (currentTracker != null && lastJob.identifier == currentTracker.executionId) {
                    logger.debug("Returning existing tracker for $jobType: ${currentTracker.trackerId}")
                    return currentTracker
                }
                val tracker = BackendJobTracker(jobType, lastJob.identifier,
                    JobExecution(lastJob, null)
                )
                logger.debug("Created BackendJobTracker for $jobType: ${tracker.trackerId}")
                activeTrackers.put(jobType, tracker)
                return tracker
            }
            return null
        }
    }

    suspend fun launchJob(jobType: JobType, arguments: List<String>) : BackendJobTracker {
        val response = VolleyPlannerApplication.volleyplannerApiClient.launchJob(jobType, arguments)
        val tracker = BackendJobTracker(jobType, response.cloudExecutionName, null)
        logger.debug("Created BackendJobTracker for $jobType: ${tracker.trackerId}")
        activeTrackers.put(jobType, tracker)
        return tracker
    }
}
